<script lang="ts">
  import type { AppController } from '../../app/controller.svelte';
  import ParamControl from '../components/ParamControl.svelte';
  import Toggle from '../components/Toggle.svelte';
  import { barLength, beatInBarOf, formatBpm, meterLabel } from '../../shared/transport';

  let { ctl, oncalibrate }: { ctl: AppController; oncalibrate: () => void } = $props();
  const t = $derived(ctl.transport);
  const mood = $derived(ctl.mood);
  const bar = $derived(barLength(t));
  const beatInBar = $derived(beatInBarOf(t));
  const auto = $derived(ctl.settings.beatDetection);
  const METERS = [
    { value: 0, label: 'Detect' },
    { value: 2, label: '2/4' },
    { value: 3, label: '3/4' },
    { value: 4, label: '4/4' },
    { value: 5, label: '5/4' },
    { value: 6, label: '6/8' },
    { value: 7, label: '7/8' },
  ];
  let fileInput: HTMLInputElement;
  let folderInput: HTMLInputElement;
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const sectionLabel: Record<string, string> = {
    unknown: '—',
    intro: 'Intro',
    groove: 'Groove',
    buildup: 'Build-up',
    drop: 'Drop',
    breakdown: 'Breakdown',
  };
</script>

<section class="sec" data-tour="source">
  <h2>Source</h2>
  <div class="now" data-testid="source-label">
    {#if ctl.source}
      <span class="dot" class:live={ctl.source.playing}></span>
      <span class="grow name">{ctl.source.label}</span>
      {#if ctl.source.canPause}
        <button data-testid="play-pause" onclick={() => ctl.togglePlay()}
          >{ctl.source.playing ? 'Pause' : 'Play'}</button
        >
      {/if}
    {:else}
      <span class="muted">No source</span>
    {/if}
  </div>
  {#if ctl.source?.canSeek && ctl.media && ctl.media.duration > 0}
    <div class="seek">
      <span class="small muted">{fmt(ctl.media.time)}</span>
      <input
        type="range"
        min="0"
        max={ctl.media.duration}
        step="0.1"
        value={ctl.media.time}
        aria-label="Position"
        onchange={(e) => ctl.seek(Number(e.currentTarget.value))}
      />
      <span class="small muted">{fmt(ctl.media.duration)}</span>
    </div>
  {/if}
  <div class="row wrap">
    <button class:active={ctl.source?.kind === 'demo'} onclick={() => ctl.start('demo')}
      >Demo</button
    >
    {#if ctl.caps.microphone}
      <button class:active={ctl.source?.kind === 'mic'} onclick={() => ctl.start('mic')}
        >Microphone</button
      >
    {/if}
    <button class:active={ctl.source?.kind === 'file'} onclick={() => fileInput.click()}
      >Files…</button
    >
    <button onclick={() => folderInput.click()}>Folder…</button>
    {#if ctl.caps.tabAudio}
      <button class:active={ctl.source?.kind === 'tab'} onclick={() => ctl.start('tab')}
        >Tab audio</button
      >
    {/if}
  </div>
  <input
    bind:this={fileInput}
    class="visually-hidden"
    aria-label="Choose files"
    type="file"
    accept="audio/*"
    multiple
    onchange={(e) => {
      const f = [...(e.currentTarget.files ?? [])];
      e.currentTarget.value = '';
      void ctl.playFiles(f);
    }}
  />
  <input
    bind:this={folderInput}
    class="visually-hidden"
    aria-label="Choose files"
    type="file"
    webkitdirectory
    multiple
    onchange={(e) => {
      const f = [...(e.currentTarget.files ?? [])];
      e.currentTarget.value = '';
      void ctl.playFiles(f);
    }}
  />

  {#if ctl.playlist.length > 1}
    <details class="sub" open>
      <summary>Playlist ({ctl.playlistIndex + 1}/{ctl.playlist.length})</summary>
      <div class="list playlist">
        {#each ctl.playlist as f, i (i)}
          <button
            class="item small"
            class:selected={i === ctl.playlistIndex}
            onclick={() => ctl.playlistGo(i)}>{i + 1}. {f.name}</button
          >
        {/each}
      </div>
    </details>
  {/if}
  {#if ctl.grid}
    <p class="hint" data-testid="grid-status">
      {#if ctl.grid.state === 'analyzing'}Analysing the whole track for beats and drops… {Math.round(
          ctl.grid.progress * 100,
        )}%
      {:else if ctl.grid.state === 'ready'}Beat grid ready: {(60 / ctl.grid.grid!.period).toFixed(
          1,
        )} BPM{ctl.grid.grid!.drops.length
          ? `, ${ctl.grid.grid!.drops.length} drop${ctl.grid.grid!.drops.length > 1 ? 's' : ''}`
          : ''}.
      {:else}Couldn’t analyse this file; using live beat tracking.{/if}
    </p>
  {/if}
</section>

<section class="sec">
  <h2>Devices</h2>
  <label class="field">
    <span>Input</span>
    <select
      value={ctl.inputDeviceId ?? ''}
      onfocus={() => ctl.refreshDevices()}
      onchange={(e) => ctl.setInputDevice(e.currentTarget.value || null)}
    >
      <option value="">Default microphone</option>
      {#each ctl.devices.inputs as d (d.id)}<option value={d.id}>{d.label}</option>{/each}
    </select>
  </label>
  {#if ctl.caps.outputSelection}
    <label class="field">
      <span>Output</span>
      <select
        value={ctl.outputDeviceId ?? ''}
        onfocus={() => ctl.refreshDevices()}
        onchange={(e) => ctl.setOutputDevice(e.currentTarget.value || null)}
      >
        <option value="">Default output</option>
        {#each ctl.devices.outputs as d (d.id)}<option value={d.id}>{d.label}</option>{/each}
      </select>
    </label>
  {/if}
  <ParamControl
    {ctl}
    def={{ id: 'inputGain', label: 'Input gain', type: 'float', min: 0, max: 4, default: 1 }}
    value={ctl.settings.inputGain}
    target="setting/inputGain"
    modulatable={false}
    onchange={(v) => ctl.setSetting('inputGain', v)}
  />
  <Toggle
    label="Play microphone through speakers"
    checked={ctl.settings.monitorInput}
    onchange={(v) => ctl.setSetting('monitorInput', v)}
  />
  <details class="sub">
    <summary>Visualize Spotify, Apple Music or any app</summary>
    <p class="hint">
      In Chrome or Edge, “Tab audio” captures a browser tab (on Windows, and on macOS 14.2+ with
      Chrome 141+, you can share the whole system’s sound). Otherwise install a loopback device: <b
        >BlackHole</b
      >
      (macOS) or <b>VB-Cable</b>
      (Windows), send your music player to it, and choose it as the Input above. Phones can only listen
      through the microphone.
    </p>
  </details>
</section>

<section class="sec" data-tour="beat">
  <div class="sec-head">
    <h2>Beat</h2>
    <span class="chip" class:accent={t.source !== 'internal'}
      >{t.source === 'midi'
        ? 'midi'
        : !auto
          ? 'held'
          : t.source === 'internal'
            ? 'detected'
            : t.source === 'grid'
              ? 'file grid'
              : t.source}</span
    >
  </div>
  <div class="beat">
    <div class="bpm" data-testid="bpm">{formatBpm(t)}<small>BPM</small></div>
    <div class="meter-sig" title="Time signature">{meterLabel(bar)}</div>
    <div class="dots" aria-hidden="true">
      {#each Array.from({ length: bar }, (_, i) => i) as b (b)}<span
          class:on={t.locked && b === beatInBar}
          class:one={b === 0}
        ></span>{/each}
    </div>
    <button
      data-testid="tap"
      onclick={() => ctl.tap()}
      title={`Tap along${ctl.keyHint('transport.tap')}`}>Tap</button
    >
  </div>
  <Toggle label="Beat detection" checked={auto} onchange={(v) => ctl.setBeatDetection(v)} />
  <p class="hint">
    {auto
      ? 'Follows the music’s tempo, beat one and time signature.'
      : 'Holding the tempo: it only changes when you tap, nudge or press Beat 1.'}
  </p>
  <label class="row meter-pick">
    <span class="small muted grow">Time signature</span>
    <select
      aria-label="Time signature"
      value={String(ctl.settings.beatsPerBar)}
      onchange={(e) => ctl.setSetting('beatsPerBar', Number(e.currentTarget.value))}
    >
      {#each METERS as m (m.value)}<option value={String(m.value)}
          >{m.value === 0 ? `Detect (now ${meterLabel(bar)})` : m.label}</option
        >{/each}
    </select>
  </label>
  <div class="meter" title="Beat-tracker confidence">
    <span style:width={`${Math.round(t.confidence * 100)}%`}></span>
  </div>
  <div class="row wrap fix">
    <button
      onclick={() => ctl.runAction('transport.nudgeBack', undefined)}
      title={`Nudge earlier${ctl.keyHint('transport.nudgeBack')}`}>−10ms</button
    >
    <button
      onclick={() => ctl.runAction('transport.nudgeForward', undefined)}
      title={`Nudge later${ctl.keyHint('transport.nudgeForward')}`}>+10ms</button
    >
    <button
      onclick={() => ctl.runAction('transport.half', undefined)}
      title={`Half tempo${ctl.keyHint('transport.half')}`}>½×</button
    >
    <button
      onclick={() => ctl.runAction('transport.double', undefined)}
      title={`Double tempo${ctl.keyHint('transport.double')}`}>2×</button
    >
    <button
      onclick={() => ctl.runAction('transport.resync', undefined)}
      title={`This is beat one${ctl.keyHint('transport.resync')}`}>Beat 1</button
    >
  </div>
</section>

<section class="sec">
  <div class="sec-head">
    <h2>Song</h2>
    <span class="chip accent" data-testid="section"
      >{sectionLabel[mood.section] ?? mood.section}</span
    >
  </div>
  {#each [['Energy', mood.energy], ['Intensity', mood.arousal], ['Brightness', mood.valence], ['Build-up', mood.buildup]] as [label, v] (label)}
    <div class="mood-row">
      <span class="small muted">{label}</span>
      <div class="meter grow">
        <span style:width={`${Math.round((v as number) * 100)}%`}></span>
      </div>
    </div>
  {/each}
  <p class="hint">
    Mood and sections are estimated from the sound. They drive the Mood modulation sources and Auto
    show.
  </p>
</section>

<section class="sec" data-tour="sync">
  <h2>Sync</h2>
  <ParamControl
    {ctl}
    def={{
      id: 'visualLatencyMs',
      label: 'Display latency',
      type: 'float',
      min: 0,
      max: 150,
      default: 30,
      step: 1,
    }}
    value={ctl.settings.visualLatencyMs}
    target="setting/visualLatencyMs"
    modulatable={false}
    onchange={(v) => ctl.setSetting('visualLatencyMs', v)}
  />
  <ParamControl
    {ctl}
    def={{
      id: 'syncOffsetMs',
      label: 'Fine offset',
      type: 'float',
      min: -250,
      max: 250,
      default: 0,
      step: 1,
    }}
    value={ctl.settings.syncOffsetMs}
    target="setting/syncOffsetMs"
    modulatable={false}
    onchange={(v) => ctl.setSetting('syncOffsetMs', v)}
  />
  <Toggle
    label="Delay playback to match visuals"
    checked={ctl.settings.delayCompensation}
    onchange={(v) => ctl.setSetting('delayCompensation', v)}
  />
  <button onclick={oncalibrate} data-testid="calibrate">Calibrate…</button>
</section>

<style>
  .now {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    min-height: 32px;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--faint);
    flex: none;
  }
  .dot.live {
    background: var(--ok);
    box-shadow: 0 0 8px var(--ok);
  }
  .seek {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
  }
  .seek input {
    width: 100%;
    accent-color: var(--accent);
  }
  .playlist {
    max-height: 180px;
    overflow-y: auto;
  }
  .playlist .item {
    text-align: left;
  }
  .beat {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 8px;
  }
  .bpm {
    font-size: 26px;
    font-weight: 600;
    font-family: var(--mono);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .meter-sig {
    font-family: var(--mono);
    color: var(--faint);
  }
  .meter-pick {
    align-items: center;
    margin-top: 6px;
  }
  .bpm small {
    font-size: 11px;
    color: var(--faint);
    margin-left: 4px;
  }
  .dots {
    display: flex;
    gap: 6px;
    flex: 1;
  }
  .dots span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
  .dots span.one {
    outline: 1px solid rgba(255, 255, 255, 0.25);
  }
  .dots span.on {
    background: var(--accent);
    box-shadow: 0 0 10px var(--accent);
  }
  .fix {
    margin-top: 10px;
  }
  .fix button {
    font-size: 12px;
    padding: 5px 8px;
  }
  .mood-row {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 8px;
    align-items: center;
    min-height: 20px;
  }
</style>
